import { Map, Record as record } from 'immutable';
import _ from 'lodash';
import joi from 'joi';
import moment from 'moment';

import t from '../../../../src/lib/I18n';
import config from '../../../../src/config';
import trainTime from '../../../../src/lib/TrainTime';
import { toPence } from '../../../../src/helpers/Currency';
import {
    DurationTypes,
    DelayRepayClaimType,
    DelayRepayClaimReason
} from '../../../../src/constants/AppConstants';

import validators from '../../../../src/validation';
import Station from '../../../../src/records/Station';
import TicketType from './TicketType';
import PurchaseLocation from './PurchaseLocation';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import { construct, fields, resolve } from '../../../../src/helpers/LinkMapper';

const fieldSchema = {
    "internalId": [ fields.string ],
    "ticketType": [ resolve.as(TicketType) ],
    "price": [ fields.string ],
    "ticketNumber": [ fields.string ],
    "validFrom": [ fields.datetime ],
    "duration": [ fields.string ],
    "customDuration": [ resolve.with(moment.duration), moment.duration() ],
    "photocardNumber": [ fields.string ],
    "smartcardNumber": [ fields.string ],
    "contactlessNumber": [ fields.string ],
    "oysterNumber": [ fields.string ],
    "imageId": [ fields.number ],
    "travelcardStation": [ resolve.with(Station) ],
    "weeklyCap": [ resolve.with(Map) ],
    "purchaseLocation": [ resolve.as(PurchaseLocation) ],
    "purchaseLocationStation": [ resolve.with(Station) ],
    "purchaseLocationOther": [ fields.string ],
    "file": [ resolve.with(Object) ],
    "xhr": [ resolve.with(Object) ],
    "uploadFailed": [ fields.bool ],
    "skipFileUpload": [ fields.bool ]
};

class CompensationTicket extends record(getDefaults(fieldSchema)) {
    static fromJSON(json = {}) {
        return construct(CompensationTicket, json, getResolvers(fieldSchema));
    }

    constructor (...args) {
        super(...args);
        return this.set('internalId', _.uniqueId('CompensationTicket'));
    }

    getComputedValues () {
        const { validFrom } = this;
        return {
            fileSize: this.file && this.file.size,
            fileType: this.file && this.file.type,
            travelcardStation: this.getIn(['travelcardStation', 'nlc']),
            purchaseLocationStation: this.getIn(['purchaseLocationStation', 'nlc']),
            validFrom: !!validFrom,
            customDuration: this.isCustomDuration ? this.seasonDuration.as('months') >= 1 : true,
            eligibleDuration: validFrom && this.seasonDuration && this.eligibleDuration.as('days') > 0
        };
    }

    getSchema () {
        const { ticketType } = this;

        let schema = {
            ticketType: joi.object().required().messages({ 'object.base': t('alertmessage.required') }),
            customDuration: validators.boolean(true, 'compensation.alert.customDuration'),
            eligibleDuration: validators.boolean(true)
        };

        if (ticketType) {
            if (ticketType.ticketNumber) {
                schema.ticketNumber = validators.regex(/^[\da-z-]{5}$/i).optional().allow(null, '');
            }
            if (ticketType.photocardNumber) {
                schema.photocardNumber = validators.requiredString(3, 20);
            }
            if (ticketType.smartcardNumber) {
                schema.smartcardNumber = validators.regex(/^\d{6}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}$/, t('delayrepay.label.smartcardNumber')).required();
            }
            if (ticketType.contactlessNumber) {
                schema.contactlessNumber = validators.requiredString(1, 20);
            }
            if (ticketType.oysterNumber) {
                schema.oysterNumber = validators.regex(/^\d{10}\s?\d{2}$/, t('compensation.label.oysterNumber')).required();
            }
            if (ticketType.acceptsUpload) {
                const uploadTypes = config.get('delayRepayUploadTypes') || [];
                const maxUploadSize = (config.get('delayRepayMaxUploadMb') || 0) * Math.pow(1024, 2);
                const minUploadSize = (config.get('delayRepayMinUploadMb') || 0) * Math.pow(1024, 2);
                const requiresUpload = !this.file && (ticketType.requiresUpload || !this.skipFileUpload);
                Object.assign(schema, {
                    imageId: requiresUpload ? validators.required() : validators.optional(),
                    fileType: validators.allowOnly(uploadTypes).allow(null).messages({
                        'any.only': t('delayrepay.error.fileType'),
                    }),
                    fileSize: joi.number().integer().min(minUploadSize).max(maxUploadSize).optional().allow(null).messages({
                        'number.base': t('alertmessage.required'),
                        'number.min': t('delayrepay.error.uploadSize.min'),
                        'number.max': t('delayrepay.error.uploadSize.max'),
                    })
                });
            }
            if (ticketType.dates) {
                Object.assign(schema, {
                    validFrom: validators.true()
                });
            }
            if (!ticketType.fixedDuration) {
                schema.duration = validators.requiredString();
            }
            if (ticketType.price) {
                schema.price = joi.number().min(0.01).max(10000).required();
            }
            if (ticketType.travelcard) {
                schema.travelcardStation = validators.requiredString();
            }
            if (ticketType.purchaseLocation) {
                const { purchaseLocation } = this;
                const requiresOther = purchaseLocation && purchaseLocation.requiresOther;
                const requiresStation = purchaseLocation && purchaseLocation.requiresStation;
                Object.assign(schema, {
                    purchaseLocation: joi.object().required().messages({ 'object.base': t('alertmessage.required') }),
                    purchaseLocationOther: requiresOther ? validators.requiredString(1, 50) : joi.any(),
                    purchaseLocationStation: requiresStation ? validators.requiredString() : joi.any()
                });
            }
        }
        return schema;
    }

    get cardNumber () {
        const { ticketType } = this;
        let cardNumber;
        if (ticketType.smartcardNumber) {
            cardNumber = this.smartcardNumber;
        } else if (ticketType.contactlessNumber) {
            cardNumber = this.contactlessNumber;
        } else if (ticketType.oysterNumber) {
            cardNumber = this.oysterNumber;
        }
        if (cardNumber) {
            cardNumber = cardNumber.replace(/[\s-]+/g, '');
        }
        return cardNumber;
    }

    get acceptsUpload () {
        const { ticketType } = this;
        return ticketType && ticketType.acceptsUpload;
    }

    get requiresPurchaseStation () {
        return this.getIn(['ticketType', 'purchaseLocation']) && this.getIn(['purchaseLocation', 'requiresStation']);
    }

    get requiresPurchaseOther () {
        return this.getIn(['ticketType', 'purchaseLocation']) && this.getIn(['purchaseLocation', 'requiresOther']);
    }

    isUploading () {
        return !!this.xhr;
    }

    get uploadType () {
        if (this.acceptsUpload) {
            return this.ticketType.statementUpload ? 'statementUpload' : 'ticketUpload';
        }
        return false;
    }

    get isCustomDuration () {
        return this.duration === DurationTypes.CUSTOM;
    }

    get customDuration () {
        return moment.duration(this.get('customDuration'));
    }

    get seasonDuration () {
        const { duration } = this;
        const durations = {
            [DurationTypes.WEEKLY]: moment.duration({ days: 7 }),
            [DurationTypes.MONTHLY]: moment.duration({ months: 1 }),
            [DurationTypes.ANNUAL]: moment.duration({ years: 1 }),
            [DurationTypes.CUSTOM]: this.customDuration
        };
        return _.get(this, 'ticketType.fixedDuration')
            || durations[duration]
            || moment.duration();
    }

    get eligibleDuration () {
        const { seasonDuration } = this;
        let { validFrom } = this;
        if (!validFrom) {
            return moment.duration();
        }
        validFrom = trainTime(validFrom);
        const validUntil = validFrom.clone().add(seasonDuration);
        const startDate = trainTime(config.get('compensationStartDate'));
        const endDate = trainTime(config.get('compensationEndDate'));
        const eligibleStart = moment.min(moment.max(validFrom, startDate), endDate);
        const eligibleEnd = moment.min(moment.max(validUntil, startDate), endDate);
        return validUntil.isBefore(startDate)
            ? moment.duration()
            : moment.duration({
                days: eligibleEnd.diff(eligibleStart, 'days') + 1
            });
    }

    get validUntil () {
        if (!this.validFrom) {
            return null;
        }
        const validFrom = this.validFrom.clone().startOf('day');
        return validFrom.add(this.seasonDuration).subtract(1, 'second');
    }

    asClaim ({origin, destination, routeCode}) {
        const { ticketType } = this;
        const additionalInformation = _.pickBy(
            Object.assign(
                {
                    routeCode: _.get(routeCode, 'code'),
                    routeDescription: _.get(routeCode, 'description')
                },
                ticketType.travelcard && {
                    travelcardNlc: _.get(this, 'travelcardStation.nlc'),
                    travelcardStation: _.get(this, 'travelcardStation.name')
                },
                ticketType.weeklyCap && {
                    CPAY: this.weeklyCap.get('name')
                }
            )
        );
        const ticketPrice = ticketType.price
            ? toPence(this.price)
            : ticketType.weeklyCap
            ? this.weeklyCap.get('price')
            : 0;
        return {
            claimType: DelayRepayClaimType.SEASON_COMPENSATION,
            ticket: {
                type: ticketType.id,
                ticketClass: ticketType.class,
                number: ticketType.ticketNumber ? this.ticketNumber : null,
                price: ticketPrice,
                photocardNumber: ticketType.photocardNumber ? this.photocardNumber : null,
                smartcardNumber: this.cardNumber || null,
                purchaseLocation: ticketType.purchaseLocation ? this.purchaseLocation.id : null,
                purchaseStation: ticketType.purchaseLocation && this.purchaseLocation.requiresStation ? this.getIn(['purchaseLocationStation', 'nlc']) : null,
                purchaseOther: ticketType.purchaseLocation && this.purchaseLocation.requiresOther ? this.purchaseLocationOther : null,
                receiptImageId: ticketType.statementUpload ? this.imageId : null,
                ticketImageId: ticketType.ticketUpload ? this.imageId : null,
                previousUpload: false
            },
            journey: {
                originStation: origin.nlc,
                destinationStation: destination.nlc,
                changeStation: [],
                departureTime: '00:00',
                departureDate: this.validFrom.format('YYYY-MM-DD'),
                scheduledArrivalDate: this.validUntil.format('YYYY-MM-DD'),
            },
            delay: {
                length: '120',
                reason: DelayRepayClaimReason.OTHER,
                additionalInformation: JSON.stringify(additionalInformation, null, 2)
            }
        };
    }

}

Object.assign(CompensationTicket.prototype, ValidationMixin);

export default CompensationTicket;
